import { withHomeState } from '../../../constants/location';
import { cleanPhone, GSTIN_RX, PHONE_RX, trimOrUndef } from '../../../utils/validation';
import type { FormErrors } from '../../../hooks/useForm';
import type { Vendor, VendorInput } from '../types/vendorTypes';

export interface VendorFormValues {
  name: string;
  contactPerson: string;
  phone: string;
  email: string;
  gstin: string;
  line: string;
  city: string;
  pincode: string;
  accountName: string;
  bankName: string;
  accountNo: string;
  ifsc: string;
  upiId: string;
  /** Paise, always positive; isAdvance flips the sign when saving */
  openingBalance: number | null;
  isAdvance: boolean;
  notes: string;
  isActive: boolean;
}

export const EMPTY_VENDOR: VendorFormValues = {
  name: '', contactPerson: '', phone: '', email: '', gstin: '', line: '', city: '', pincode: '',
  accountName: '', bankName: '', accountNo: '', ifsc: '', upiId: '', openingBalance: null, isAdvance: false, notes: '', isActive: true,
};

export const fromVendor = (v: Vendor): VendorFormValues => ({
  ...EMPTY_VENDOR,
  name: v.name ?? '', contactPerson: v.contactPerson ?? '', phone: v.phone ?? '', email: v.email ?? '', gstin: v.gstin ?? '',
  line: v.address?.line ?? '', city: v.address?.city ?? '', pincode: v.address?.pincode ?? '',
  accountName: v.bankDetails?.accountName ?? '', bankName: v.bankDetails?.bankName ?? '', accountNo: v.bankDetails?.accountNo ?? '',
  ifsc: v.bankDetails?.ifsc ?? '', upiId: v.bankDetails?.upiId ?? '',
  openingBalance: v.openingBalance ? Math.abs(v.openingBalance) : null, isAdvance: (v.openingBalance ?? 0) < 0,
  notes: v.notes ?? '', isActive: v.isActive ?? true,
});

export function validateVendor(form: VendorFormValues): FormErrors {
  const errs: FormErrors = {};
  if (!form.name.trim()) errs.name = 'Enter the supplier name';
  if (form.phone && !PHONE_RX.test(cleanPhone(form.phone))) errs.phone = 'Enter a 10 digit mobile number';
  if (form.gstin && !GSTIN_RX.test(form.gstin.toUpperCase())) errs.gstin = 'GSTIN should be 15 characters, like 27ABCDE1234F1Z5';
  if (form.pincode && !/^\d{6}$/.test(form.pincode)) errs.pincode = 'Pincode should be 6 digits';
  if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) errs.email = 'Enter a valid email';
  return errs;
}

export const toVendorInput = (form: VendorFormValues, editing: boolean): VendorInput => ({
  name: form.name.trim(),
  contactPerson: trimOrUndef(form.contactPerson),
  phone: trimOrUndef(cleanPhone(form.phone)),
  email: form.email.trim(),
  gstin: form.gstin.trim().toUpperCase(),
  address: withHomeState({ line: form.line.trim(), city: form.city.trim(), pincode: form.pincode.trim() }),
  bankDetails: { accountName: form.accountName.trim(), bankName: form.bankName.trim(), accountNo: form.accountNo.trim(), ifsc: form.ifsc.trim().toUpperCase(), upiId: form.upiId.trim() },
  openingBalance: form.openingBalance ? (form.isAdvance ? -form.openingBalance : form.openingBalance) : 0,
  notes: trimOrUndef(form.notes),
  ...(editing ? { isActive: form.isActive } : {}),
});

/** Backend error paths -> form field names */
export const VENDOR_SERVER_FIELDS: Record<string, string> = { 'address.pincode': 'pincode', 'address.line': 'line', 'address.city': 'city' };
